import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAMS = ["KIDS", "TEENS", "ALL LEVELS"];

export default function TrialDialog({ open, onOpenChange, defaultProgram = "" }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [program, setProgram] = useState(defaultProgram);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && defaultProgram) setProgram(defaultProgram);
  }, [open, defaultProgram]);

  const reset = () => {
    setName(""); setPhone(""); setEmail(""); setProgram(""); setMessage("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Por favor ingresa tu nombre.");
      return;
    }
    if (!phone.trim() && !email.trim()) {
      toast.error("Indica al menos un teléfono o email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        program: program || null,
        message: message.trim() || null,
      });
      toast.success("Solicitud enviada. Te abrimos WhatsApp.");
      reset();
      onOpenChange(false);
      // Open WhatsApp in a new tab
      if (data?.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar. Intenta de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error de validación.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={LANDING.trialDialog}
        className="max-w-lg border border-black/10 rounded-[4px] p-0 overflow-hidden bg-white text-black"
      >
        <div className="p-7 border-b border-black/10">
          <DialogHeader className="space-y-2 text-left">
            <p className="font-mono-acc text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              / Clase de prueba gratis
            </p>
            <DialogTitle className="font-display text-3xl text-black">
              Reserva tu primera clase.
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-600">
              Completa tus datos y abriremos WhatsApp para confirmar día y hora contigo.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} data-testid={LANDING.trialForm} className="p-7 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="trial-name" className="font-mono-acc text-[10px] uppercase tracking-[0.2em] text-neutral-500">Nombre</Label>
            <Input
              id="trial-name"
              data-testid={LANDING.trialName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre completo"
              className="rounded-[2px] border-black/20 focus-visible:ring-black"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trial-phone" className="font-mono-acc text-[10px] uppercase tracking-[0.2em] text-neutral-500">Teléfono</Label>
              <Input
                id="trial-phone"
                data-testid={LANDING.trialPhone}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+56 9 ..."
                className="rounded-[2px] border-black/20 focus-visible:ring-black"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trial-email" className="font-mono-acc text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</Label>
              <Input
                id="trial-email"
                data-testid={LANDING.trialEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tunombre@correo.cl"
                className="rounded-[2px] border-black/20 focus-visible:ring-black"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-mono-acc text-[10px] uppercase tracking-[0.2em] text-neutral-500">Programa</Label>
            <Select value={program} onValueChange={setProgram}>
              <SelectTrigger
                data-testid={LANDING.trialProgram}
                className="rounded-[2px] border-black/20 focus:ring-black"
              >
                <SelectValue placeholder="Selecciona un programa" />
              </SelectTrigger>
              <SelectContent className="rounded-[2px]">
                {PROGRAMS.map((p) => (
                  <SelectItem key={p} value={p} className="rounded-[2px]">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="trial-msg" className="font-mono-acc text-[10px] uppercase tracking-[0.2em] text-neutral-500">Mensaje (opcional)</Label>
            <Textarea
              id="trial-msg"
              data-testid={LANDING.trialMessage}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="¿Tienes experiencia previa? ¿Algún horario preferido?"
              rows={3}
              className="rounded-[2px] border-black/20 focus-visible:ring-black resize-none"
            />
          </div>

          <DialogFooter className="pt-2 gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              data-testid={LANDING.trialClose}
              onClick={() => onOpenChange(false)}
              className="btn-sharp rounded-[2px] border-black/20 hover:bg-neutral-100"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={LANDING.trialSubmit}
              disabled={loading}
              className="btn-sharp rounded-[2px] bg-black text-white hover:bg-neutral-800 font-mono-acc uppercase tracking-[0.18em] text-xs px-6 py-5"
            >
              {loading ? "Enviando..." : "Enviar y abrir WhatsApp"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
